import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import Typography from '../../../../../styles/Typography';
import { StyledDefaultRadioProps } from '../../types';

export const StyledRadioWrapper = styled.span`
  display: block;
  width: 100%;
`;

export const StyledRadioControlWrapper = styled.span`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.5rem 0.625rem;
  border-radius: 0.5rem;
  transition: ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  margin-bottom: 0.5rem;

  &:hover {
    background-color: ${({ theme }) => rgba(theme.colors.accent, 0.06)};
  }
`;

export const StyledRadioMark = styled.span<StyledDefaultRadioProps>`
  height: 1rem;
  width: 1rem;
  border-radius: 100%;
  transition: ${({ theme }) => theme.transitions.default};
  ${({ theme, checked }) =>
    checked
      ? css`
          border: 5px solid ${theme.colors.brand.yellow};
          box-shadow: 0 0 0 3px ${rgba(theme.colors.brand.yellow, 0.32)};
        `
      : css`
          border: 1.5px solid ${theme.colors.radio.mark.inActive};
        `};
  flex-shrink: 0;
`;

export const StyledRadioLabel = styled(Typography).attrs({
  forwardedAs: 'span',
  variant: 'body',
})`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-left: 0.625rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => rgba(theme.colors.text.secondary, 0.7)};
`;

export const StyledRadioDecorWrapper = styled.figure`
  display: inline-flex;
  height: 1.75rem;
  width: 1.75rem;
  flex-shrink: 0;
  border-radius: 100%;
  background-color: ${({ theme }) => theme.colors.background.light};
  border: 1px solid ${({ theme }) => rgba(theme.colors.text.primary, 0.12)};
  overflow: hidden;
  justify-content: center;
  align-items: center;
  margin-left: 0.75rem;
`;

export const StyledRadioDecor = styled.img`
  height: auto;
  width: 100%;
  max-width: 100%;
  display: block;
`;

export const StyledRadioCheckboxesList = styled.ul`
  list-style: none;
  ${({ theme }) =>
    theme.direction === 'rtl' ? 'padding: 0.25rem 0 0;' : 'padding: 0.25rem 0 0;'}
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

interface ChipItemProps {
  tagColor?: string;
  isChecked?: boolean;
}

/**
 * Two display modes:
 * - tagColor present: status-style chip (per-status color tinting, hidden
 *   checkbox, gradient bg when active, ✓ glyph). Used by StatusSelect.
 * - tagColor absent: plain row (subtle hover bg, native checkbox visible).
 *   Used by the workspace switcher and other generic multi-selects.
 */
export const StyledRadioCheckboxesListItem = styled.li<ChipItemProps>`
  width: 100%;
  position: relative;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 180ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 180ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1);

  & label {
    width: 100%;
    padding: 0.5rem 0.625rem;
    cursor: pointer;
    border-radius: inherit;
  }

  & label span {
    font-size: 0.875rem;
    font-weight: 500;
    letter-spacing: -0.005em;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  /* Plain mode (no tagColor — generic selector) */
  ${({ tagColor, isChecked, theme }) =>
    !tagColor &&
    css`
      border: 1px solid transparent;
      background: ${isChecked
        ? rgba(theme.colors.accent, 0.06)
        : 'transparent'};
      &:hover {
        background: ${rgba(theme.colors.accent, 0.05)};
      }
    `}

  /* Chip mode (tagColor present — status filter) */
  ${({ tagColor, isChecked, theme }) =>
    tagColor &&
    css`
      border: 1.5px solid
        ${isChecked ? tagColor : rgba(theme.colors.text.primary, 0.08)};
      background: ${isChecked
        ? `linear-gradient(135deg, ${rgba(tagColor, 0.16)}, ${rgba(
            tagColor,
            0.06
          )})`
        : theme.colors.background.light};
      ${isChecked &&
      css`
        box-shadow: 0 4px 12px -4px ${rgba(tagColor, 0.32)},
          inset 0 0 0 1px ${rgba(tagColor, 0.18)};
        transform: translateY(-1px);
      `}

      &:hover {
        transform: translateY(-1px);
        border-color: ${tagColor};
        background: ${isChecked
          ? `linear-gradient(135deg, ${rgba(tagColor, 0.22)}, ${rgba(
              tagColor,
              0.1
            )})`
          : rgba(tagColor, 0.05)};
        box-shadow: 0 4px 12px -4px ${rgba(tagColor, 0.22)};
      }

      & label {
        padding: 0.625rem 0.875rem;
      }

      & label > div:first-of-type {
        display: none;
      }
      & label > div:first-of-type + div {
        margin-left: 0;
      }

      & label [class*='StyledColorTag'],
      & label > span > div:first-child {
        width: 0.75rem !important;
        height: 0.75rem !important;
        box-shadow: 0 0 0 4px
          ${isChecked ? rgba(tagColor, 0.25) : rgba(tagColor, 0.12)};
        transition: box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1);
      }

      & label span {
        font-weight: 600;
        color: ${isChecked ? tagColor : theme.colors.text.primary};
      }

      ${isChecked &&
      css`
        &::after {
          content: '✓';
          position: absolute;
          right: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.875rem;
          font-weight: 700;
          color: ${tagColor};
        }
      `}
    `}
`;
